import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { PLAN_PRICES, formatPlanPrice } from '../config/plans';

/**
 * Plan prices for the visitor's region, for DISPLAY.
 *
 * The server picks a purchasing-power tier from the caller's IP
 * (functions/src/regionalPricing.ts) and createPaymentIntent charges that same
 * tier, so the checkout total always comes from the server, never from here.
 * Until the server answers, and in prerendered HTML, the full price is shown.
 */

// v2: Oct 2026 plan line-up (3 months + Premium). A v1 entry still holds the
// retired monthly price, so it must not be read.
const CACHE_KEY = 'logami.regionalPricing.v2';
const FULL_PRICING = { tier: 'full', country: null, prices: PLAN_PRICES };

let inflight = null;

function readCache() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeCache(value) {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch (_) {
    /* storage unavailable: the next page asks again */
  }
}

function loadRegionalPricing() {
  if (!inflight) {
    inflight = httpsCallable(functions, 'getRegionalPricing')()
      .then(({ data }) => {
        if (!data?.prices) return FULL_PRICING;
        writeCache(data);
        return data;
      })
      .catch(() => FULL_PRICING);
  }
  return inflight;
}

export function useRegionalPricing() {
  const [pricing, setPricing] = useState(() => readCache() || FULL_PRICING);

  useEffect(() => {
    // Playwright prerender sets navigator.webdriver: keep the full EUR price in
    // the static HTML that search engines index.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return undefined;
    if (readCache()) return undefined;
    let active = true;
    loadRegionalPricing().then((next) => {
      if (active) setPricing(next);
    });
    return () => {
      active = false;
    };
  }, []);

  return useMemo(() => {
    const priceOf = (planId) => pricing.prices?.[planId] ?? PLAN_PRICES[planId] ?? null;
    return {
      tier: pricing.tier,
      isRegional: pricing.tier !== 'full',
      getPrice: priceOf,
      getPriceLabel: (planId) => formatPlanPrice(priceOf(planId)),
    };
  }, [pricing]);
}
